import { useEffect, useState } from 'react';
import { collection, getDocs } from 'firebase/firestore';
import { db } from '../lib/firebase';

/**
 * useListBoardMap — builds a `{ listId -> boardId }` map across ALL of
 * the user's boards. Used by features that need to resolve a list's
 * parent board from outside the current-board scope, like:
 *
 *   - the agenda's "open in source list" jump for old reminders that
 *     don't yet have sourceBoardId stored on the doc;
 *   - the board dropdown's "this board has reminders" marker (we
 *     check against sourceBoardId; if missing, fall back to the
 *     map's resolved value).
 *
 * Implementation: on mount and whenever `boards` changes, run one
 * `getDocs` per board and merge the results. N queries for N boards
 * — fine at Walter scale (single-digit boards). Uses simple `getDocs`
 * (no live listener) since the map only changes when lists are
 * created or deleted, which already trigger renders elsewhere.
 *
 * Returns:
 *   {
 *     map: { [listId]: boardId, ... },
 *     loading: boolean,
 *   }
 */
export function useListBoardMap(userId, boards) {
  const [map, setMap] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!userId || !boards || boards.length === 0) {
      setMap({});
      setLoading(false);
      return undefined;
    }
    let cancelled = false;
    setLoading(true);
    const next = {};
    Promise.all(
      boards.map(b =>
        getDocs(collection(db, 'users', userId, 'boards', b.id, 'lists'))
          .then(snap => {
            for (const d of snap.docs) next[d.id] = b.id;
          })
          .catch(err => {
            console.warn('[useListBoardMap] read failed for board', b.id, err);
          }),
      ),
    ).then(() => {
      if (cancelled) return;
      setMap(next);
      setLoading(false);
    });
    return () => { cancelled = true; };
  }, [userId, boards]);

  return { map, loading };
}
